import ReactGA from 'react-ga4';

const GA_MEASUREMENT_ID = 'G-G1940E3XXN';

// Initialize GA4 via react-ga4
// send_page_view: false so only our RouteTracker sends page views with correct titles
export const initGA = () => {
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gtagOptions: { send_page_view: false },
  });
};

// ─── PAGE VIEW TRACKING ───────────────────────────────────────────
// Sends a page_view with a unique title so GA shows each page separately
export const trackPageView = (path, title) => {
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title,
  });
};

// ─── ROUTE → TITLE MAPPING ───────────────────────────────────────
// Returns a human-readable title for every route so GA4 shows them individually
const GAME_NAMES = {
  'another-one-piece': 'One Piece',
  naruto: 'Naruto',
  bleach: 'Bleach',
  'one-punch': 'One Punch Man',
  'dragon-ball': 'Dragon Ball',
};

const ANIME_HUB_PAGES = {
  'top-airing': 'Top Airing Anime',
  'top-anime': 'Top Rated Anime',
  'top-upcoming': 'Top Upcoming Anime',
  'most-popular': 'Most Popular Anime',
  'most-favorited': 'Most Favorited Anime',
  characters: 'Anime Characters',
  search: 'Anime Search',
  'all-animes': 'All Animes',
};

export const getPageTitle = (pathname) => {
  // Games — keyword first, brand last (SEO best practice)
  if (pathname === '/games') return 'Anime Character Guessing Games - ChibiDash';
  const gameMatch = pathname.match(/^\/games\/(.+)$/);
  if (gameMatch) {
    const gameName = GAME_NAMES[gameMatch[1]] || gameMatch[1];
    return `${gameName} Character Guessing Game - ChibiDash`;
  }

  // Game History
  if (pathname === '/game-history') return 'Game History - ChibiDash';
  const historyMatch = pathname.match(/^\/game-history\/(.+)$/);
  if (historyMatch) {
    const gameName = GAME_NAMES[historyMatch[1]] || historyMatch[1];
    return `${gameName} Game History - ChibiDash`;
  }

  // Anime Hub
  if (pathname === '/animehub') return 'Anime Hub - Top Anime, Airing & Upcoming | ChibiDash';
  const animePageMatch = pathname.match(/^\/animehub\/(.+)$/);
  if (animePageMatch) {
    const page = animePageMatch[1];
    if (page.startsWith('anime/')) {
      return 'Anime Details - ChibiDash'; // will be overridden by component
    }
    const hubTitle = ANIME_HUB_PAGES[page];
    if (hubTitle) return `${hubTitle} - ChibiDash`;
  }

  // Blog
  if (pathname === '/new-blog-home') return 'Anime Blog - Reviews & Articles | ChibiDash';
  if (pathname.startsWith('/new-blog-home/article/'))
    return 'Anime Review - ChibiDash Blog'; // will be overridden by component

  // Manga
  if (pathname === '/manga-home') return 'Manga Hub - Read & Explore Manga | ChibiDash';
  if (pathname.startsWith('/manga-home/manga/'))
    return 'Manga Details - ChibiDash';

  // Community
  if (pathname === '/community') return 'Anime Community - ChibiDash';
  if (pathname.startsWith('/community/'))
    return 'Anime Community - ChibiDash';

  // News
  if (pathname === '/the-news') return 'Anime News - ChibiDash';
  if (pathname.startsWith('/the-news/'))
    return 'Anime News Article - ChibiDash';

  // Profile
  if (pathname.startsWith('/profile')) return 'Profile - ChibiDash';

  // Auth
  if (pathname === '/auth' || pathname === '/auth/login') return 'Login - ChibiDash';
  if (pathname === '/auth/signup') return 'Sign Up - ChibiDash';

  return 'ChibiDash - Anime Games, Quizzes & Community Hub';
};

// ─── GAME EVENT TRACKING ──────────────────────────────────────────
export const trackGameStart = (gameName, theme) => {
  ReactGA.event({
    category: 'Game',
    action: 'game_start',
    label: gameName,
    game_name: gameName,
    game_theme: theme,
  });
};

export const trackGameAnswer = (gameName, isCorrect, questionNumber, characterName) => {
  ReactGA.event({
    category: 'Game',
    action: isCorrect ? 'correct_answer' : 'incorrect_answer',
    label: `${gameName} - Q${questionNumber} - ${characterName}`,
    game_name: gameName,
    question_number: questionNumber,
    character_name: characterName,
  });
};

export const trackGameOver = (gameName, theme, score, correctAnswers, incorrectAnswers, totalTime) => {
  ReactGA.event({
    category: 'Game',
    action: 'game_over',
    label: gameName,
    value: score,
    game_name: gameName,
    game_theme: theme,
    score: score,
    correct_answers: correctAnswers,
    incorrect_answers: incorrectAnswers,
    total_time_seconds: totalTime,
  });
};

export const trackGamePass = (gameName, questionNumber) => {
  ReactGA.event({
    category: 'Game',
    action: 'game_pass',
    label: `${gameName} - Q${questionNumber}`,
    game_name: gameName,
    question_number: questionNumber,
  });
};

export const trackGameRestart = (gameName) => {
  ReactGA.event({
    category: 'Game',
    action: 'game_restart',
    label: gameName,
    game_name: gameName,
  });
};

export const trackGameTimeout = (gameName, questionNumber) => {
  ReactGA.event({
    category: 'Game',
    action: 'game_timeout',
    label: `${gameName} - Q${questionNumber}`,
    game_name: gameName,
    question_number: questionNumber,
  });
};

// ─── ANIME HUB TRACKING ──────────────────────────────────────────
export const trackAnimeView = (animeId, animeTitle) => {
  ReactGA.event({
    category: 'Anime',
    action: 'anime_view',
    label: animeTitle,
    anime_id: animeId,
    anime_title: animeTitle,
  });
};

export const trackAnimeSearch = (searchQuery, resultsCount) => {
  ReactGA.event({
    category: 'Anime',
    action: 'anime_search',
    label: searchQuery,
    search_query: searchQuery,
    results_count: resultsCount,
  });
};

export const trackAnimeCardClick = (animeId, animeTitle, fromPage) => {
  ReactGA.event({
    category: 'Anime',
    action: 'anime_card_click',
    label: animeTitle,
    anime_id: animeId,
    from_page: fromPage,
  });
};

// ─── BLOG TRACKING ───────────────────────────────────────────────
export const trackBlogView = (articleId, articleTitle) => {
  ReactGA.event({
    category: 'Blog',
    action: 'blog_article_view',
    label: articleTitle,
    article_id: articleId,
    article_title: articleTitle,
  });
};

// ─── MANGA TRACKING ──────────────────────────────────────────────
export const trackMangaView = (mangaId, mangaTitle) => {
  ReactGA.event({
    category: 'Manga',
    action: 'manga_view',
    label: mangaTitle,
    manga_id: mangaId,
    manga_title: mangaTitle,
  });
};

// ─── COMMUNITY TRACKING ──────────────────────────────────────────
export const trackCommunityAction = (action, label) => {
  ReactGA.event({
    category: 'Community',
    action: action,
    label: label,
  });
};

// ─── NEWS TRACKING ───────────────────────────────────────────────
export const trackNewsView = (articleTitle) => {
  ReactGA.event({
    category: 'News',
    action: 'news_article_view',
    label: articleTitle,
  });
};

// ─── NAVIGATION TRACKING ─────────────────────────────────────────
export const trackNavClick = (section, destination) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'nav_click',
    label: `${section} → ${destination}`,
  });
};

// ─── OUTBOUND LINK TRACKING ──────────────────────────────────────
export const trackOutboundLink = (url, label) => {
  ReactGA.event({
    category: 'Outbound',
    action: 'outbound_click',
    label: label || url,
    transport: 'beacon',
  });
};

// ─── USER ENGAGEMENT ─────────────────────────────────────────────
export const trackEngagement = (action, label, value) => {
  ReactGA.event({
    category: 'Engagement',
    action: action,
    label: label,
    value: value,
  });
};
